import { useCallback, useEffect, useMemo, useState } from 'react'
import type { EvaluationAnalysisPayload } from '@/hooks/use-evaluation-analysis'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useArtifactCanvas } from '@/components/artifact/artifact-canvas-context'
import { RubricCaseFile } from '@/components/admin/evaluation-rubrics/rubric-case-file'
import { RubricDimensionsCanvas } from '@/components/admin/evaluation-rubrics/rubric-dimensions-canvas'
import { RubricDraftStatusBar } from '@/components/admin/evaluation-rubrics/rubric-draft-status-bar'
import { RubricFocusAreaCanvas } from '@/components/admin/evaluation-rubrics/rubric-focus-area-canvas'
import { RubricRequirementsCanvas } from '@/components/admin/evaluation-rubrics/rubric-requirements-canvas'
import { RubricRevisionHistoryCanvas } from '@/components/admin/evaluation-rubrics/rubric-revision-history-canvas'
import {
  RubricSampleMatchCanvas,
  type RubricSamplePreviewMeta,
} from '@/components/admin/evaluation-rubrics/rubric-sample-match-canvas'
import { RubricSampleTestCard } from '@/components/admin/evaluation-rubrics/rubric-sample-test-card'
import { RubricScoringCanvas } from '@/components/admin/evaluation-rubrics/rubric-scoring-canvas'
import {
  RUBRIC_SECTION_IDS,
  rubricSectionHighlight,
  type RubricSectionId,
} from '@/components/admin/evaluation-rubrics/rubric-section-ids'
import type { ScoringConfig } from '@/components/admin/evaluation-rubrics/rubric-read-helpers'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getPageHeader } from '@/constants'
import {
  SCORING_PRESETS,
  buildPreviewAnalysisPayload,
  buildPublishPayload,
  diffCriteria,
  validateCriteriaDraft,
  type ScoringPresetKey,
} from '@/lib/rubric-publish-payload'
import {
  useCurrentRubric,
  useDuplicateRubricCorpus,
  usePublishRubricRevision,
  useRubricCorpus,
  useRubricPreviewRun,
  useSkillDomain,
  useSkillDomains,
  type RubricCriterion,
} from '@/hooks/use-evaluation-rubrics-admin'
import { useAdminBasePath, adminPath } from '@/components/admin/use-admin-base-path'

const ONBOARDING_KEY = 'rubric-builder-onboarded'

type SkillDomain = {
  key?: string
  label?: string
  skills?: string[]
}

type EvaluationRubricBuilderProps = {
  corpusId?: string
  onStartConversation?: (message: string) => void
}

function isRubricSectionId(value: string | null): value is RubricSectionId {
  return Boolean(value && RUBRIC_SECTION_IDS.includes(value as RubricSectionId))
}

function draftFingerprint(criteria: RubricCriterion[], scoring: ScoringConfig) {
  return JSON.stringify({ criteria, scoring })
}

export function EvaluationRubricBuilder({
  corpusId,
  onStartConversation,
}: EvaluationRubricBuilderProps) {
  const basePath = useAdminBasePath()
  const navigate = useNavigate()
  const skillsCatalogPath = adminPath(basePath, 'skills-catalog')
  const listPath = adminPath(basePath, 'evaluation-rubrics')
  const [searchParams] = useSearchParams()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const currentQuery = useCurrentRubric()
  const corpusQuery = useRubricCorpus(corpusId)
  const { data: corpus, isLoading, error, refetch } = corpusId ? corpusQuery : currentQuery
  const publish = usePublishRubricRevision()
  const duplicateCorpus = useDuplicateRubricCorpus()
  const previewRun = useRubricPreviewRun()
  const [publishOpen, setPublishOpen] = useState(false)
  const [changeSummary, setChangeSummary] = useState('')
  const [draftCriteria, setDraftCriteria] = useState<RubricCriterion[]>([])
  const [publishedCriteria, setPublishedCriteria] = useState<RubricCriterion[]>([])
  const [scoringPreset, setScoringPreset] = useState<ScoringPresetKey | 'custom'>('balanced')
  const [draftScoring, setDraftScoring] = useState<ScoringConfig>({
    structured_weight: 0.6,
    semantic_weight: 0.4,
    llm_threshold: 0.7,
  })
  const [publishedScoring, setPublishedScoring] = useState<ScoringConfig>({
    structured_weight: 0.6,
    semantic_weight: 0.4,
    llm_threshold: 0.7,
  })
  const [advancedJsonOpen, setAdvancedJsonOpen] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [draftJson, setDraftJson] = useState('')
  const [highlightedSection, setHighlightedSection] = useState<RubricSectionId | null>(null)
  const [previewPayload, setPreviewPayload] = useState<EvaluationAnalysisPayload | null>(null)
  const [previewSubjectLabel, setPreviewSubjectLabel] = useState<string | undefined>()
  const [previewMeta, setPreviewMeta] = useState<RubricSamplePreviewMeta | undefined>()
  const [sampleFingerprint, setSampleFingerprint] = useState<string | null>(null)
  const [demoDataHint, setDemoDataHint] = useState<string | null>(null)
  const currentRevision = corpus?.current_revision ?? null
  const skillDomains = useMemo(
    () => (currentRevision?.payload?.skill_domains as SkillDomain[] | undefined) ?? [],
    [currentRevision],
  )
  const domainKey = corpus?.domain_key || 'employment'
  const { data: skillDomainSummaries } = useSkillDomains(domainKey)
  const firstDomainId = skillDomainSummaries?.[0]?.id
  const { data: firstDomainDetail } = useSkillDomain(firstDomainId)
  const skillOptions = useMemo(() => {
    const fromSnapshot = skillDomains.flatMap((domain) => domain.skills ?? [])
    const fromCatalog = firstDomainDetail?.skills.map((skill) => skill.name) ?? []
    return Array.from(new Set([...fromSnapshot, ...fromCatalog])).sort((a, b) =>
      a.localeCompare(b),
    )
  }, [skillDomains, firstDomainDetail])

  useEffect(() => {
    if (typeof window === 'undefined') return
    setShowOnboarding(window.localStorage.getItem(ONBOARDING_KEY) !== '1')
  }, [])

  useEffect(() => {
    const section = searchParams.get('section')
    if (!isRubricSectionId(section)) return
    const target = document.getElementById(rubricSectionHighlight(section))
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [searchParams, corpus?.id])

  useEffect(() => {
    if (currentRevision?.criteria) {
      const cloned = structuredClone(currentRevision.criteria)
      setDraftCriteria(cloned)
      setPublishedCriteria(structuredClone(currentRevision.criteria))
      const scoring = (currentRevision.payload?.scoring as ScoringConfig | undefined) ?? {}
      const normalizedScoring = {
        structured_weight: scoring.structured_weight ?? 0.6,
        semantic_weight: scoring.semantic_weight ?? 0.4,
        llm_threshold: scoring.llm_threshold ?? 0.7,
      }
      setDraftScoring(normalizedScoring)
      setPublishedScoring(structuredClone(normalizedScoring))
      const presetMatch = (
        Object.entries(SCORING_PRESETS) as Array<[ScoringPresetKey, typeof SCORING_PRESETS.balanced]>
      ).find(
        ([, preset]) =>
          preset.structured_weight === (scoring.structured_weight ?? 0.6)
          && preset.semantic_weight === (scoring.semantic_weight ?? 0.4)
          && preset.llm_threshold === (scoring.llm_threshold ?? 0.7),
      )
      setScoringPreset(presetMatch ? presetMatch[0] : 'custom')
    }
  }, [currentRevision?.id, currentRevision?.criteria, currentRevision?.payload])

  const validationMessage = useMemo(() => validateCriteriaDraft(draftCriteria), [draftCriteria])
  const hasUnsavedChanges = useMemo(
    () => {
      if (!currentRevision) return false
      return JSON.stringify(draftCriteria) !== JSON.stringify(publishedCriteria)
        || JSON.stringify(draftScoring) !== JSON.stringify(publishedScoring)
    },
    [currentRevision, draftCriteria, publishedCriteria, draftScoring, publishedScoring],
  )
  const criteriaDiff = useMemo(
    () => diffCriteria(publishedCriteria, draftCriteria),
    [publishedCriteria, draftCriteria],
  )
  const mockPreviewPayload = useMemo(
    () =>
      buildPreviewAnalysisPayload(
        draftCriteria,
        skillDomains,
        corpus?.methodology_id ?? 'employment_match',
      ),
    [draftCriteria, skillDomains, corpus?.methodology_id],
  )

  const sampleIsStale = useMemo(() => {
    if (!previewPayload || !sampleFingerprint) return false
    return sampleFingerprint !== draftFingerprint(draftCriteria, draftScoring)
  }, [draftCriteria, draftScoring, previewPayload, sampleFingerprint])

  const header = getPageHeader(
    corpusId ? adminPath(basePath, `evaluation-rubrics/${corpusId}`) : listPath,
    corpus?.label || 'Rubric builder',
    'Configure criteria, performance levels, and scoring for analysis reports.',
  )

  const flashSection = useCallback((section: RubricSectionId) => {
    setHighlightedSection(section)
    window.setTimeout(() => setHighlightedSection(null), 3000)
  }, [])

  const saveCriteria = useCallback(
    (nextCriteria: RubricCriterion[], section: RubricSectionId) => {
      setDraftCriteria(nextCriteria)
      closeCanvas()
      flashSection(section)
    },
    [closeCanvas, flashSection],
  )

  const openDimensionsCanvas = useCallback(() => {
    openCanvas({
      preventAutoFocus: true,
      title: 'Advanced — match dimensions',
      content: (
        <RubricDimensionsCanvas
          criteria={draftCriteria}
          skillOptions={skillOptions}
          onSave={(next) => saveCriteria(next, 'dimensions')}
          onClose={closeCanvas}
        />
      ),
    })
  }, [closeCanvas, draftCriteria, openCanvas, saveCriteria, skillOptions])

  const openFocusCanvas = useCallback(
    (focusCriterionKey?: string) => {
      openCanvas({
        preventAutoFocus: true,
        title: 'Levels & skills',
        content: (
          <RubricFocusAreaCanvas
            criteria={draftCriteria}
            skillOptions={skillOptions}
            focusCriterionKey={focusCriterionKey}
            onSave={(next) => saveCriteria(next, 'focus')}
            onClose={closeCanvas}
          />
        ),
      })
    },
    [closeCanvas, draftCriteria, openCanvas, saveCriteria, skillOptions],
  )

  const openRequirementsCanvas = useCallback(() => {
    openCanvas({
      preventAutoFocus: true,
      title: 'Advanced — requirements',
      content: (
        <RubricRequirementsCanvas
          criteria={draftCriteria}
          onSave={(next) => saveCriteria(next, 'requirements')}
          onClose={closeCanvas}
        />
      ),
    })
  }, [closeCanvas, draftCriteria, openCanvas, saveCriteria])

  const openScoringCanvas = useCallback(() => {
    openCanvas({
      preventAutoFocus: true,
      title: 'Scoring blend',
      content: (
        <RubricScoringCanvas
          scoring={draftScoring}
          scoringPreset={scoringPreset}
          onSave={(nextScoring, nextPreset) => {
            setDraftScoring(nextScoring)
            setScoringPreset(nextPreset)
            closeCanvas()
            flashSection('scoring')
          }}
          onClose={closeCanvas}
        />
      ),
    })
  }, [closeCanvas, draftScoring, flashSection, openCanvas, scoringPreset])

  const openHistoryCanvas = useCallback(() => {
    openCanvas({
      preventAutoFocus: true,
      title: 'Revision history',
      content: (
        <RubricRevisionHistoryCanvas
          revisions={corpus?.revisions ?? []}
          publishedCriteria={publishedCriteria}
          draftCriteria={draftCriteria}
          onClose={closeCanvas}
        />
      ),
    })
  }, [closeCanvas, corpus?.revisions, draftCriteria, openCanvas, publishedCriteria])

  const openFullReportCanvas = useCallback(() => {
    if (sampleIsStale) {
      toast.error('Sample report is stale — run sample analysis again.')
      return
    }
    const payload = previewPayload ?? mockPreviewPayload
    openCanvas({
      preventAutoFocus: true,
      title: 'Sample analysis report',
      content: (
        <RubricSampleMatchCanvas
          payload={payload}
          previewMeta={previewMeta}
          subjectLabel={previewSubjectLabel}
          validationMessage={validationMessage}
          isSampleRun={Boolean(previewPayload)}
        />
      ),
    })
  }, [
    mockPreviewPayload,
    openCanvas,
    previewMeta,
    previewPayload,
    previewSubjectLabel,
    sampleIsStale,
    validationMessage,
  ])
  function dismissOnboarding() {
    window.localStorage.setItem(ONBOARDING_KEY, '1')
    setShowOnboarding(false)
  }

  function openPublishDialog() {
    if (!currentRevision || !corpus) return
    const payload = buildPublishPayload(
      currentRevision.payload,
      draftCriteria,
      corpus.methodology_id,
      draftScoring,
    )
    setDraftJson(JSON.stringify(payload, null, 2))
    setChangeSummary('')
    setAdvancedJsonOpen(false)
    setPublishOpen(true)
  }

  async function handlePublish() {
    if (!corpus?.id) return
    const validation = validateCriteriaDraft(draftCriteria)
    if (validation && !advancedJsonOpen) {
      toast.error(validation)
      return
    }

    let payload: Record<string, unknown>
    if (advancedJsonOpen) {
      try {
        payload = JSON.parse(draftJson) as Record<string, unknown>
      } catch {
        toast.error('Invalid JSON payload')
        return
      }
    } else {
      payload = buildPublishPayload(
        currentRevision?.payload ?? {},
        draftCriteria,
        corpus.methodology_id,
        draftScoring,
      )
    }

    try {
      const result = await publish.mutateAsync({
        corpusId: corpus.id,
        payload,
        changeSummary: changeSummary.trim(),
      })
      toast.success(
        `Published v${result.version} (${result.stale_datums_marked} evaluations marked stale)`,
      )
      setPublishOpen(false)
      setPublishedCriteria(structuredClone(draftCriteria))
      setPublishedScoring(structuredClone(draftScoring))
      void refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Publish failed')
    }
  }

  async function handleSamplePreview() {
    if (!corpus?.id || !currentRevision) return
    if (validationMessage) {
      toast.error(validationMessage)
      return
    }
    const payload = buildPublishPayload(
      currentRevision.payload,
      draftCriteria,
      corpus.methodology_id,
      draftScoring,
    )
    try {
      setDemoDataHint(null)
      const result = await previewRun.mutateAsync({
        corpusId: corpus.id,
        payload,
      })
      setPreviewPayload(result.payload)
      setPreviewSubjectLabel(result.preview_meta?.subject_label)
      setPreviewMeta(result.preview_meta)
      setSampleFingerprint(draftFingerprint(draftCriteria, draftScoring))
      flashSection('test')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sample analysis failed'
      if (message.includes('demo_data_required')) {
        setDemoDataHint(message.replace(/^demo_data_required:\s*/i, ''))
      }
      toast.error(message.replace(/^demo_data_required:\s*/i, ''))
    }
  }

  return (
    <PageContainer className="pb-24">
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={header.title}
          description={header.description}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {corpusId ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={duplicateCorpus.isPending}
                  onClick={() => {
                    void (async () => {
                      try {
                        const copy = await duplicateCorpus.mutateAsync({
                          corpusId,
                          label: corpus ? `${corpus.label} (copy)` : undefined,
                        })
                        toast.success(`Duplicated ${copy.label}`)
                        navigate(adminPath(basePath, `evaluation-rubrics/${copy.id}`))
                      } catch (err) {
                        toast.error(
                          err instanceof Error ? err.message : 'Could not duplicate rubric',
                        )
                      }
                    })()
                  }}
                >
                  Duplicate
                </Button>
              ) : null}
              <Button type="button" variant="outline" asChild>
                <Link to={listPath}>All rubrics</Link>
              </Button>
            </div>
          }
        />
      </PageHeaderStack>

      {showOnboarding ? (
        <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm space-y-2 mb-4">
          <p>
            <strong className="text-foreground">Edit on the page</strong> for names, definitions,
            weights, and add/remove/reorder. Use the pencil (or Levels & skills) for deep ladder and
            scoring work in an artifact canvas. Ask the agent in chat to draft criteria via{' '}
            <code className="text-xs">manage_rubrics</code>.{' '}
            <strong className="text-foreground">Sample analysis</strong> runs a real pass-1
            evaluation against demo seed data — re-run after edits. Publish creates a new revision.
          </p>
          <Button type="button" variant="ghost" size="inlineLink" onClick={dismissOnboarding}>
            Got it
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading rubric…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load rubric'}
        </p>
      ) : corpus ? (
        <>
          <RubricCaseFile
            label={corpus.label}
            methodologyId={corpus.methodology_id}
            status={corpus.status}
            revisionVersion={currentRevision?.version}
            validationMessage={validationMessage}
            draftCriteria={draftCriteria}
            scoring={draftScoring}
            scoringPreset={scoringPreset}
            skillDomains={skillDomains}
            skillsCatalogPath={skillsCatalogPath}
            revisions={corpus.revisions}
            highlightedSection={highlightedSection}
            onChangeCriteria={setDraftCriteria}
            onAskAgent={onStartConversation}
            onEditDimensionsAdvanced={openDimensionsCanvas}
            onEditFocusLevelsSkills={openFocusCanvas}
            onEditRequirementsAdvanced={openRequirementsCanvas}
            onEditScoring={openScoringCanvas}
            onViewAllHistory={openHistoryCanvas}
            testCard={
              <div className="space-y-3">
                {demoDataHint ? (
                  <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm space-y-2">
                    <p className="text-muted-foreground">{demoDataHint}</p>
                    <Button type="button" variant="outline" size="sm" asChild>
                      <Link to="/platform/simulation">Create demo pack</Link>
                    </Button>
                  </div>
                ) : null}
                <RubricSampleTestCard
                  subjectLabel={previewSubjectLabel}
                  isRunning={previewRun.isPending}
                  errorMessage={
                    previewRun.error instanceof Error
                      ? previewRun.error.message.replace(/^demo_data_required:\s*/i, '')
                      : null
                  }
                  payload={previewPayload ?? undefined}
                  isStale={sampleIsStale}
                  highlighted={highlightedSection === 'test'}
                  onRun={() => void handleSamplePreview()}
                  onOpenFullReport={openFullReportCanvas}
                />
              </div>
            }
          />

          <RubricDraftStatusBar
            className="mt-6"
            revisionVersion={currentRevision?.version}
            validationMessage={validationMessage}
            hasUnsavedChanges={hasUnsavedChanges}
            publishPending={publish.isPending}
            canPublish={Boolean(currentRevision)}
            onPreview={openFullReportCanvas}
            onPublish={openPublishDialog}
          />
        </>
      ) : null}

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Publish new revision</DialogTitle>
            <DialogDescription>
              Review changes before publishing. Open evaluation datasets will be marked stale.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {criteriaDiff.added.length > 0 ? (
              <p>Added: {criteriaDiff.added.map((c) => c.label || c.key).join(', ')}</p>
            ) : null}
            {criteriaDiff.removed.length > 0 ? (
              <p>Removed: {criteriaDiff.removed.map((c) => c.label || c.key).join(', ')}</p>
            ) : null}
            {criteriaDiff.changed.length > 0 ? (
              <p>Changed: {criteriaDiff.changed.map((c) => c.key).join(', ')}</p>
            ) : null}
            {criteriaDiff.added.length === 0
            && criteriaDiff.removed.length === 0
            && criteriaDiff.changed.length === 0 ? (
              <p className="text-muted-foreground">Scoring or metadata changes only.</p>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="change-summary">Change summary</Label>
              <Input
                id="change-summary"
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                placeholder="Describe what changed in this revision"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="px-0"
              onClick={() => setAdvancedJsonOpen((open) => !open)}
            >
              {advancedJsonOpen ? 'Hide advanced JSON' : 'Edit JSON (advanced)'}
            </Button>
            {advancedJsonOpen ? (
              <Textarea
                value={draftJson}
                onChange={(e) => setDraftJson(e.target.value)}
                className="min-h-[280px] font-mono text-xs"
                spellCheck={false}
              />
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPublishOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handlePublish()} disabled={publish.isPending}>
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
